/**
 * GTD Semantic Navigation
 * 
 * Implements domain-specific semantic navigation patterns for the Getting Things Done methodology.
 * This provides intelligent, context-aware navigation hints that understand GTD workflows and relationships.
 * 
 * Core GTD Semantic Relationships:
 * - Capture → Clarify → Organize → Reflect → Engage (GTD workflow)
 * - Projects contain Next Actions
 * - Actions belong to Contexts
 * - Projects have Outcomes
 * - References support Actions and Projects
 * 
 * @author GTD Domain Team
 * @since GTD Domain v1.3.0
 */

import {
  IDomainSemantics,
  IEntityRelationship,
  IWorkflowDefinition,
  SemanticRelationType,
  ContextualInformation,
  NavigationHints,
  DomainContext,
  McpServerId,
} from '@swoft/platform-contracts';

import { BaseNavigationHintsBuilder } from '@swoft/navigation-utils';

// ============================================
// GTD-SPECIFIC SEMANTIC DEFINITIONS
// ============================================

/**
 * GTD Entity Types in the ubiquitous language
 */
export type GTDEntityType = 
  | 'inbox_item'
  | 'project' 
  | 'next_action'
  | 'context'
  | 'outcome'
  | 'reference'
  | 'someday_maybe'
  | 'waiting_for'
  | 'calendar_item';

/**
 * GTD Context Types for semantic grouping
 */
export type GTDContextType = 
  | '@computer'
  | '@phone'
  | '@errands' 
  | '@office'
  | '@home'
  | '@agenda'
  | '@waiting'
  | '@someday';

/**
 * GTD Processing States for workflow navigation
 */
export type GTDProcessingState = 
  | 'captured'      // In inbox, not processed
  | 'clarified'     // Processed, determined actionable
  | 'organized'     // Filed in appropriate lists
  | 'reviewed'      // Part of weekly review
  | 'engaged'       // Currently working on
  | 'completed'     // Finished
  | 'archived';     // Stored for reference

// ============================================
// GTD ENTITY RELATIONSHIPS
// ============================================

/**
 * Standard GTD entity relationships following David Allen's methodology
 */
export const GTD_ENTITY_RELATIONSHIPS: IEntityRelationship[] = [
  // Project → Next Actions relationship
  {
    sourceEntityType: 'project',
    targetEntityType: 'next_action',
    relationshipType: 'contains',
    description: 'Projects contain one or more next actions to move toward completion',
    cardinality: 'one-to-many',
    availableActions: [
      {
        action: 'view_next_actions',
        toolName: 'gtd_actions_by_project',
        relevanceContext: 'When viewing a project and need to see actionable next steps'
      },
      {
        action: 'create_next_action',
        toolName: 'gtd_action_create',
        relevanceContext: 'When a project needs additional next actions to move forward'
      },
      {
        action: 'review_project_progress',
        toolName: 'gtd_project_review',
        relevanceContext: 'During weekly review or when checking project momentum'
      }
    ]
  },

  // Next Action → Context relationship
  {
    sourceEntityType: 'next_action',
    targetEntityType: 'context',
    relationshipType: 'belongs_to',
    description: 'Next actions are organized by the context where they can be completed',
    cardinality: 'many-to-one',
    availableActions: [
      {
        action: 'view_context_actions',
        toolName: 'gtd_actions_by_context',
        relevanceContext: 'When in a specific context and ready to take action'
      },
      {
        action: 'change_context',
        toolName: 'gtd_action_update',
        relevanceContext: 'When circumstances change where an action can be completed'
      }
    ]
  },

  // Inbox Item → Project/Action relationship
  {
    sourceEntityType: 'inbox_item',
    targetEntityType: 'project',
    relationshipType: 'triggers',
    description: 'Inbox items can trigger the creation of new projects during clarification',
    availableActions: [
      {
        action: 'convert_to_project',
        toolName: 'gtd_project_create',
        relevanceContext: 'When an inbox item requires multiple actions to complete'
      },
      {
        action: 'extract_next_action',
        toolName: 'gtd_action_create',
        relevanceContext: 'When an inbox item can be completed with a single action'
      },
      {
        action: 'defer_processing',
        toolName: 'gtd_inbox_defer',
        relevanceContext: 'When an inbox item needs more information before processing'
      }
    ]
  },

  // Reference → Action/Project relationship
  {
    sourceEntityType: 'reference',
    targetEntityType: 'next_action',
    relationshipType: 'enables',
    description: 'Reference materials enable the completion of actions and projects',
    cardinality: 'many-to-many',
    availableActions: [
      {
        action: 'attach_reference',
        toolName: 'gtd_reference_attach',
        relevanceContext: 'When an action needs supporting documentation'
      },
      {
        action: 'create_reference',
        toolName: 'gtd_reference_create',
        relevanceContext: 'When processing reveals need for reference material'
      }
    ]
  },

  // Waiting For → Person relationship (cross-domain)
  {
    sourceEntityType: 'waiting_for',
    targetEntityType: 'person',
    relationshipType: 'depends_on',
    description: 'Waiting For items depend on specific people for completion',
    availableActions: [
      {
        action: 'find_responsible_person',
        toolName: 'party_list_persons',
        relevanceContext: 'When tracking who is responsible for a waiting item'
      },
      {
        action: 'follow_up_reminder',
        toolName: 'gtd_waiting_follow_up',
        relevanceContext: 'When a waiting item needs follow-up action'
      }
    ]
  }
];

// ============================================
// GTD WORKFLOWS
// ============================================

/**
 * Core GTD workflows following David Allen's 5-step methodology
 */
export const GTD_WORKFLOWS: IWorkflowDefinition[] = [
  // Core GTD Processing Workflow
  {
    workflowId: 'gtd_capture_clarify_organize',
    name: 'GTD Capture → Clarify → Organize Workflow',
    steps: [
      {
        stepId: 'capture',
        description: 'Capture everything that crosses your mind into a trusted system',
        tools: ['gtd_inbox_capture', 'gtd_mind_sweep'],
        completionCriteria: ['All open loops captured', 'Mind feels clear'],
        nextSteps: ['clarify']
      },
      {
        stepId: 'clarify',
        description: 'Process inbox items: What is it? Is it actionable?',
        tools: ['gtd_inbox_clarify', 'gtd_process_inbox'],
        completionCriteria: ['Item nature understood', 'Actionability determined'],
        nextSteps: ['organize_actionable', 'organize_non_actionable']
      },
      {
        stepId: 'organize_actionable',
        description: 'Organize actionable items into appropriate lists',
        tools: ['gtd_project_create', 'gtd_action_create', 'gtd_calendar_add'],
        completionCriteria: ['Item in appropriate action list', 'Context assigned'],
        nextSteps: ['reflect']
      },
      {
        stepId: 'organize_non_actionable',
        description: 'File non-actionable items appropriately',
        tools: ['gtd_reference_create', 'gtd_someday_add', 'gtd_trash'],
        completionCriteria: ['Item properly filed', 'Reference accessible'],
        nextSteps: ['reflect']
      },
      {
        stepId: 'reflect',
        description: 'Review system regularly to maintain trust and currency',
        tools: ['gtd_weekly_review', 'gtd_daily_review'],
        completionCriteria: ['System up to date', 'Priorities clear'],
        nextSteps: ['engage']
      },
      {
        stepId: 'engage',
        description: 'Choose actions confidently based on context, time, and energy',
        tools: ['gtd_actions_by_context', 'gtd_next_action_choose'],
        completionCriteria: ['Action completed', 'Progress made'],
        nextSteps: ['capture'] // Continuous cycle
      }
    ],
    triggers: [
      {
        trigger: 'new_inbox_item',
        context: { source: 'any', urgency: 'any' }
      },
      {
        trigger: 'mind_sweep_session',
        context: { scheduled: true, type: 'weekly_review' }
      },
      {
        trigger: 'overwhelm_feeling',
        context: { stress_level: 'high', clarity: 'low' }
      }
    ],
    outcomes: [
      'Clear mind with trusted system',
      'All commitments captured and organized',
      'Confident action choices',
      'Reduced stress and increased productivity'
    ]
  },

  // Weekly Review Workflow
  {
    workflowId: 'gtd_weekly_review',
    name: 'GTD Weekly Review Process',
    steps: [
      {
        stepId: 'collect',
        description: 'Gather loose papers, materials, and digital items',
        tools: ['gtd_mind_sweep', 'gtd_inbox_capture'],
        completionCriteria: ['All loose items captured', 'Physical inbox empty'],
        nextSteps: ['process']
      },
      {
        stepId: 'process',
        description: 'Process all captured items to zero',
        tools: ['gtd_process_inbox', 'gtd_inbox_clarify'],
        completionCriteria: ['Inbox at zero', 'All items processed'],
        nextSteps: ['review']
      },
      {
        stepId: 'review',
        description: 'Review action lists, calendar, and waiting fors',
        tools: ['gtd_actions_review', 'gtd_calendar_review', 'gtd_waiting_review'],
        completionCriteria: ['Lists current', 'Waiting items followed up'],
        nextSteps: ['update']
      },
      {
        stepId: 'update',
        description: 'Update project lists and someday/maybe items',
        tools: ['gtd_project_list', 'gtd_someday_review'],
        completionCriteria: ['Projects current', 'Someday items evaluated'],
        nextSteps: ['plan']
      },
      {
        stepId: 'plan',
        description: 'Review bigger picture goals and plan next week',
        tools: ['gtd_natural_planning', 'gtd_goal_review'],
        completionCriteria: ['Week planned', 'Priorities set'],
        nextSteps: ['engage']
      }
    ],
    triggers: [
      {
        trigger: 'weekly_scheduled_time',
        context: { day: 'friday', time: 'afternoon' }
      },
      {
        trigger: 'system_feels_out_of_control',
        context: { stress_level: 'high', system_trust: 'low' }
      }
    ],
    outcomes: [
      'System completely current and trustworthy',
      'Clear priorities for upcoming week',
      'Reduced mental overhead',
      'Increased confidence in commitments'
    ]
  },

  // Natural Planning Workflow
  {
    workflowId: 'gtd_natural_planning',
    name: 'GTD Natural Planning Model',
    steps: [
      {
        stepId: 'purpose',
        description: 'Define why you are doing this project',
        tools: ['gtd_natural_planning'],
        completionCriteria: ['Purpose clearly articulated', 'Motivation understood'],
        nextSteps: ['vision']
      },
      {
        stepId: 'vision',
        description: 'Envision successful outcome - what does done look like?',
        tools: ['gtd_natural_planning'],
        completionCriteria: ['Success criteria defined', 'Vision compelling'],
        nextSteps: ['brainstorm']
      },
      {
        stepId: 'brainstorm',
        description: 'Generate ideas without judgment or organization',
        tools: ['gtd_natural_planning', 'gtd_mind_sweep'],
        completionCriteria: ['All ideas captured', 'Creative thinking exhausted'],
        nextSteps: ['organize']
      },
      {
        stepId: 'organize',
        description: 'Structure ideas into logical sequence and priorities',
        tools: ['gtd_project_create', 'gtd_action_create'],
        completionCriteria: ['Project structure clear', 'Next actions defined'],
        nextSteps: ['next_actions']
      },
      {
        stepId: 'next_actions',
        description: 'Identify immediate next physical actions',
        tools: ['gtd_action_create', 'gtd_actions_by_context'],
        completionCriteria: ['Next actions specific', 'Actions assigned contexts'],
        nextSteps: ['engage']
      }
    ],
    triggers: [
      {
        trigger: 'new_project_identified',
        context: { complexity: 'medium_to_high', clarity: 'low' }
      },
      {
        trigger: 'project_stuck',
        context: { progress: 'stalled', next_action: 'unclear' }
      }
    ],
    outcomes: [
      'Clear project definition and scope',
      'Compelling vision of success',
      'Logical action sequence',
      'Immediate next steps identified'
    ]
  }
];

// ============================================
// GTD NAVIGATION HINTS BUILDER
// ============================================

/**
 * GTD-specific NavigationHints builder that understands GTD workflows and relationships
 */
export class GTDNavigationHintsBuilder extends BaseNavigationHintsBuilder {
  
  protected getDefaultContext(): ContextualInformation {
    return {
      domain: 'gtd',
      operation_type: 'productivity',
      user_journey: 'gtd_workflow',
      workflow_state: 'ready_for_action'
    };
  }

  protected getDefaultMetadata(): NavigationHints['metadata'] {
    return {
      generated_by: 'gtd-semantic-navigation',
      version: '1.0.0',
      domain_methodology: 'david_allen_gtd',
      semantic_model: 'gtd_ubiquitous_language'
    };
  }

  /**
   * Add GTD-specific workflow navigation
   */
  addGTDWorkflowHints(
    currentStep: string,
    workflowId: string = 'gtd_capture_clarify_organize'
  ): GTDNavigationHintsBuilder {
    const workflow = GTD_WORKFLOWS.find(w => w.workflowId === workflowId);
    if (!workflow) return this;

    const currentStepDef = workflow.steps.find(s => s.stepId === currentStep);
    if (!currentStepDef) return this;

    // Add current step tools as related tools
    currentStepDef.tools.forEach(tool => {
      this.addRelatedTool({
        tool_name: tool,
        description: `${tool} - ${currentStepDef.description}`,
        relevance_score: 0.9,
        usage_context: `Current step: ${currentStep}`
      });
    });

    // Add next steps
    if (currentStepDef.nextSteps) {
      currentStepDef.nextSteps.forEach(nextStepId => {
        const nextStep = workflow.steps.find(s => s.stepId === nextStepId);
        if (nextStep) {
          this.addNextStep(`${nextStep.stepId}: ${nextStep.description}`);
        }
      });
    }

    return this;
  }

  /**
   * Add context-aware action suggestions
   */
  addContextualActionHints(
    context: GTDContextType,
    energyLevel: 'high' | 'medium' | 'low' = 'medium'
  ): GTDNavigationHintsBuilder {
    // Add context-specific tool suggestions
    this.addRelatedTool({
      tool_name: 'gtd_actions_by_context',
      description: `View actions available in ${context} context`,
      relevance_score: 0.95,
      usage_context: `Current context: ${context}, Energy: ${energyLevel}`
    });

    // Add energy-appropriate suggestions
    if (energyLevel === 'low') {
      this.addNextStep('Consider low-energy tasks like organizing or reviewing');
      this.addRelatedTool({
        tool_name: 'gtd_reference_review',
        description: 'Review reference materials (low energy activity)',
        relevance_score: 0.8,
        usage_context: 'Low energy level'
      });
    } else if (energyLevel === 'high') {
      this.addNextStep('Tackle challenging projects or creative work');
      this.addRelatedTool({
        tool_name: 'gtd_project_advance',
        description: 'Advance high-priority projects (high energy activity)',
        relevance_score: 0.9,
        usage_context: 'High energy level'
      });
    }

    return this;
  }

  /**
   * Add cross-domain handoffs for GTD workflow
   */
  addGTDCrossDomainHandoffs(
    entityType: GTDEntityType,
    entityId: string
  ): GTDNavigationHintsBuilder {
    switch (entityType) {
      case 'waiting_for':
        this.addPersonAssignmentHandoff(
          'responsible person for waiting item',
          'mcp__gtd-mcp__'
        );
        break;

      case 'project':
        // DevOps handoff for technical projects
        this.addContextHandoff('technical_implementation', {
          mcp: 'mcp__devops-mcp__',
          tool: 'health_check',
          suggested_action: 'Check if this project relates to system health',
          scenario: 'Project may have technical dependencies',
          pre_populated: {
            context: `gtd_project_${entityId}`
          },
          workflow_context: {
            operation_type: 'planning',
            severity: 'medium',
            domain_context: {
              source_mcp: 'mcp__gtd-mcp__',
              integration_type: 'project_technical_assessment'
            }
          }
        });
        break;

      case 'next_action':
        // Party management for action assignment
        this.addPersonAssignmentHandoff(
          'action assignee',
          'mcp__gtd-mcp__'
        );
        break;
    }

    return this;
  }
}

// ============================================
// DOMAIN SEMANTICS CONFIGURATION
// ============================================

/**
 * GTD Domain semantic configuration for the semantic registry
 */
export const GTD_DOMAIN_SEMANTICS: IDomainSemantics = {
  domainId: 'gtd',
  mcpServerId: 'mcp__gtd-mcp__',
  entityRelationships: GTD_ENTITY_RELATIONSHIPS,
  workflows: GTD_WORKFLOWS,
  customSemantics: [
    {
      patternId: 'gtd_processing_state_transition',
      description: 'Semantic navigation based on GTD processing states',
      applicableContext: {
        processing_state: ['captured', 'clarified', 'organized', 'reviewed']
      },
      navigationHints: {
        next_steps: [
          'Follow GTD processing workflow',
          'Consider current processing state',
          'Choose appropriate next action'
        ],
        context: {
          domain: 'gtd',
          operation_type: 'processing',
          user_journey: 'gtd_state_transition'
        }
      }
    },
    {
      patternId: 'gtd_context_energy_matching',
      description: 'Match actions to current context and energy level',
      applicableContext: {
        context_type: 'any',
        energy_level: ['high', 'medium', 'low'],
        time_available: 'any'
      },
      navigationHints: {
        next_steps: [
          'Filter actions by current context',
          'Consider available energy level',
          'Choose actions matching time available'
        ],
        context: {
          domain: 'gtd',
          operation_type: 'action_selection',
          user_journey: 'context_energy_optimization'
        }
      }
    }
  ],
  confidence: 0.95,
  lastUpdated: new Date().toISOString()
};

// ============================================
// FACTORY FUNCTIONS
// ============================================

/**
 * Create a new GTD NavigationHints builder
 */
export function createGTDNavigationBuilder(): GTDNavigationHintsBuilder {
  return new GTDNavigationHintsBuilder();
}

/**
 * Create GTD semantic navigation hints for a specific context
 */
export function createGTDSemanticHints(
  context: {
    entityType?: GTDEntityType;
    entityId?: string;
    currentContext?: GTDContextType;
    energyLevel?: 'high' | 'medium' | 'low';
    workflowStep?: string;
    workflowId?: string;
  }
): NavigationHints {
  const builder = createGTDNavigationBuilder();

  // Add workflow hints if in a workflow
  if (context.workflowStep) {
    builder.addGTDWorkflowHints(context.workflowStep, context.workflowId);
  }

  // Add contextual action hints
  if (context.currentContext) {
    builder.addContextualActionHints(context.currentContext, context.energyLevel);
  }

  // Add cross-domain handoffs
  if (context.entityType && context.entityId) {
    builder.addGTDCrossDomainHandoffs(context.entityType, context.entityId);
  }

  return builder.build();
}

// ============================================
// TYPE EXPORTS
// ============================================

export type {
  GTDEntityType,
  GTDContextType,
  GTDProcessingState
};